/* eslint-disable max-lines -- TODO: Split this file into smaller modules */
import { navigate } from "gatsby"
import queryString from "query-string"
import { last, omit } from "radash"

import type { PaymentType } from "~config/constants"
import { HUBSPOT_URL, QUERY_STRING_PROPS } from "~config/constants"
import { ResourceType, type AutomationType } from "~graphql/generated/types"
import {
    getUrlQueryParam,
    getUrlQueryParamsObj,
    navigateToSearchParams,
} from "~utils/helpers/helpers"

export const CONTACT_LIST_CREATE_MODE_SELECTION = "selection" as const

export const TAB_QUERY_PARAM = "tab"
export const CONTACTS_TAB_VALUES = {
    CONTACTS: "contacts",
    CONTACT_LISTS: "contact-lists",
    IMPORTS: "imports",
    SENT_BLASTS: "sent-blasts",
} as const

export const ROUTES = getRoutes()

// NAVIGATION
export function navigateWithHardReload() {
    window.location.href = "/"
}

export function navigateToRoute(
    route: string,
    options?: { replace?: boolean; state?: Record<string, unknown> }
) {
    return navigate(route, options)
}

export function navigateToContactLists() {
    return navigate(
        `${ROUTES.CONTACTS}?${TAB_QUERY_PARAM}=${CONTACTS_TAB_VALUES.CONTACT_LISTS}`
    )
}

export function navigateToContacts() {
    return navigate(
        `${ROUTES.CONTACTS}?${TAB_QUERY_PARAM}=${CONTACTS_TAB_VALUES.CONTACTS}`
    )
}

export function navigateToContactImports() {
    return navigate(
        `${ROUTES.CONTACTS}?${TAB_QUERY_PARAM}=${CONTACTS_TAB_VALUES.IMPORTS}`
    )
}

export function navigateToSentBlasts() {
    return navigate(
        `${ROUTES.CONTACTS}?${TAB_QUERY_PARAM}=${CONTACTS_TAB_VALUES.SENT_BLASTS}`
    )
}

function navigateBackOrToPage(route: string) {
    void navigate(window.history.state?.back ?? route)
}

export function navigateBackOrToSendQuote() {
    void navigateBackOrToPage(ROUTES.QUOTES)
}

export function navigateToListing(listingId: string) {
    return navigate(`/manage-listings/${listingId}`)
}

export function navigateToOutfitter(outfitterId: string) {
    return navigate(`/outfitter/${outfitterId}`)
}

export function navigateToContact(contactId: string) {
    return navigate(getContactUrl(contactId))
}

export function navigateToQuickPayDetail(
    paymentId: string,
    options?: { replace?: boolean }
) {
    return navigate(`${ROUTES.QUICK_PAY}/${paymentId}`, options)
}

export function navigateToCustomProperties() {
    return navigate(ROUTES.CONTACT_CUSTOM_PROPERTIES)
}

export function navigateToNewContactList(options?: {
    readonly mode?: typeof CONTACT_LIST_CREATE_MODE_SELECTION
}) {
    if (options?.mode === CONTACT_LIST_CREATE_MODE_SELECTION) {
        const params = new URLSearchParams({
            [QUERY_STRING_PROPS.CONTACT_LIST_CREATE]:
                CONTACT_LIST_CREATE_MODE_SELECTION,
        })
        return navigate(`${ROUTES.NEW_CONTACT_LIST}?${params.toString()}`)
    }
    return navigate(ROUTES.NEW_CONTACT_LIST)
}

export function isContactListSelectionCreateMode(search?: string | null) {
    if (!search) {
        return false
    }
    const normalized = search.startsWith("?") ? search : `?${search}`
    const value = new URLSearchParams(normalized).get(
        QUERY_STRING_PROPS.CONTACT_LIST_CREATE
    )
    return value === CONTACT_LIST_CREATE_MODE_SELECTION
}

export function navigateToContactList(contactListId: string) {
    return navigate(getContactListUrl(contactListId))
}

function getContactListUrl(contactListId: string) {
    return `/contacts/lists/${contactListId}`
}

export function navigateToNewBooking(source: string) {
    const url = getNewBookingUrl({ source })
    return navigate(url)
}

function getNewBookingUrl({
    source,
    leadId,
    contactId,
}: {
    source: string
    leadId?: string
    contactId?: string
}) {
    const params = new URLSearchParams()
    params.set("source", source)

    if (leadId) {
        params.set(QUERY_STRING_PROPS.LEAD_ID, leadId)
    }

    if (contactId) {
        params.set(QUERY_STRING_PROPS.CONTACT_ID, contactId)
    }

    const searchString = params.toString()
    return `/booking/new?${searchString}`
}

export function navigateToBooking(bookingId?: string, action?: string) {
    return navigate(getBookingUrl(bookingId, action))
}

export function navigateToAdditionalPayment(bookingId: string) {
    return navigate(`/booking/${bookingId}/additional-payment`)
}

export function navigateToForms() {
    return navigate(ROUTES.FORMS)
}

export function navigateToBookingInviteRequestDetails(quoteRequestId: string) {
    return navigateToSearchParams({
        [QUERY_STRING_PROPS.QUOTE_REQUEST_ID]: quoteRequestId,
    })
}

export function navigateToAuctions() {
    return navigate(ROUTES.AUCTIONS)
}

export function navigateToAuction(auctionId: string) {
    return navigate(`/auction/${auctionId}`)
}

export function navigateToSourceTrips() {
    return navigate(ROUTES.SOURCE_TRIPS)
}

/**
 * Removes a query param from the current URL and navigates to the updated path.
 * Reusable for any param (e.g. quote_request_id, filters).
 */
function navigateWithoutParam(paramName: string) {
    const params = getUrlQueryParamsObj()
    const rest = omit(params, [paramName])
    const search = queryString.stringify(rest)
    const pathname =
        typeof window !== "undefined" ? window.location.pathname : ""
    const path = search ? `${pathname}?${search}` : pathname
    void navigate(path, { replace: true })
}

export function clearQuoteRequestIdFromUrl() {
    return navigateWithoutParam(QUERY_STRING_PROPS.QUOTE_REQUEST_ID)
}

export function clearEditTemplateIdFromUrl() {
    return navigateWithoutParam(QUERY_STRING_PROPS.EDIT_TEMPLATE_ID)
}

export function clearFormSubmissionIdFromUrl() {
    return navigateWithoutParam(QUERY_STRING_PROPS.FORM_SUBMISSION_ID)
}

export function navigateToQuickBooksPage() {
    return navigate(`/integrations/quick-books`)
}

export function navigateToMetaPage() {
    return navigate(ROUTES.META)
}

export function navigateToSmsPage() {
    return navigate(ROUTES.SMS)
}

export function navigateToCustomEmailDomainPage() {
    return navigate(ROUTES.CUSTOM_EMAIL_DOMAIN)
}

export function navigateToWebsiteWidgetPage() {
    return navigate(ROUTES.WEBSITE_WIDGET)
}

export function navigateToSmsConnectPage() {
    return navigate(ROUTES.SMS_CONNECT)
}

export function navigateToCalendarIntegrationsPage() {
    return navigate(ROUTES.CALENDAR_INTEGRATIONS)
}

export function navigateToNewForm(options?: {
    replace?: boolean
    state?: Record<string, unknown>
}) {
    return navigate(ROUTES.NEW_FORM, options)
}

export function navigateToCreateListing() {
    return navigate(ROUTES.CREATE_LISTING)
}

export function navigateToNewPaidFacebookAd() {
    return navigate(ROUTES.NEW_PAID_FACEBOOK_AD)
}

export function navigateToPaidFacebookAds() {
    return navigate(ROUTES.PAID_FACEBOOK_ADS)
}

export function navigateToEditForm({
    formId,
    options,
}: {
    formId: string
    options?: { replace?: boolean; state?: Record<string, unknown> }
}) {
    return navigate(getEditFormUrl(formId), options)
}

function getEditFormUrl(formId: string) {
    return `${ROUTES.EDIT_FORM}/${formId}`
}

export function isNewFormPathname(pathname: string) {
    const normalized = normalizePathname(pathname)

    return normalized === ROUTES.NEW_FORM
}

export function isEditFormPathname({
    pathname,
    formId,
}: {
    pathname: string
    formId: string
}) {
    const normalized = normalizePathname(pathname)
    const editFormPath = getEditFormUrl(formId)

    return (
        normalized === editFormPath || normalized.startsWith(`${editFormPath}/`)
    )
}

export function getEditFormIdFromPathname(
    pathname: string
): string | undefined {
    const normalized = normalizePathname(pathname)
    if (!normalized.startsWith(`${ROUTES.EDIT_FORM}/`)) return undefined

    const rest = normalized.slice(ROUTES.EDIT_FORM.length + 1)
    const idSegment = rest.split("/")[0]

    return idSegment || undefined
}

export function navigateToFormDetails({ formId }: { formId: string }) {
    return navigate(`${ROUTES.FORMS}/${formId}`)
}

export function navigateToAdditionalBookingPayment({
    bookingId,
    amount,
    paymentType,
    shouldDefaultToQuoteSubTotal,
}: {
    bookingId: string
    amount?: number
    paymentType?: PaymentType
    shouldDefaultToQuoteSubTotal?: boolean
}) {
    if (shouldDefaultToQuoteSubTotal) {
        return navigateWithDefaultToSubTotalUrl({ bookingId, paymentType })
    }

    const baseUrl = getAdditionalBookingPaymentBaseUrl({ bookingId })

    if (!amount) return navigate(baseUrl)

    let url = `${baseUrl}?${QUERY_STRING_PROPS.AMOUNT}=${amount}`

    if (paymentType) {
        url = `${url}&${QUERY_STRING_PROPS.PAYMENT_TYPE}=${paymentType}`
    }

    return navigate(url)
}

export function navigateToTemplateDetails(templateId: string) {
    return navigate(getTemplateDetailsUrl(templateId))
}

export function navigateToEditEmailTemplate(
    templateId: string,
    options?: { replace?: boolean; state?: Record<string, unknown> }
) {
    const url = getEditEmailTemplateUrl(templateId)

    return options ? navigate(url, options) : navigate(url)
}

export function navigateToEditSmsTemplate(templateId: string) {
    return navigate(getEditSmsTemplateUrl(templateId))
}

export function navigateToAutomationDetail(automationType: AutomationType) {
    return navigate(getAutomationDetailUrl(automationType))
}

export function getAutomationDetailUrl(automationType: AutomationType) {
    return `${ROUTES.AUTOMATION_CENTER}/${automationType}`
}

export function getTemplateDetailsUrl(templateId: string) {
    return `${ROUTES.TEMPLATES}/${templateId}`
}

export function getTemplatesUrl() {
    return ROUTES.TEMPLATES
}

export function getEditEmailTemplateUrl(templateId: string) {
    return `${ROUTES.EDIT_TEMPLATE}/${templateId}`
}

export function isEditEmailTemplatePathname({
    pathname,
    templateId,
}: {
    pathname: string
    templateId: string
}) {
    const normalized = normalizePathname(pathname)
    const editTemplatePath = getEditEmailTemplateUrl(templateId)
    return (
        normalized === editTemplatePath ||
        normalized.startsWith(`${editTemplatePath}/`)
    )
}

export function getEditEmailTemplateIdFromPathname(
    pathname: string
): string | undefined {
    const normalized = normalizePathname(pathname)
    if (!normalized.startsWith(`${ROUTES.EDIT_TEMPLATE}/`)) return undefined

    const rest = normalized.slice(ROUTES.EDIT_TEMPLATE.length + 1)
    const idSegment = rest.split("/")[0]

    return idSegment || undefined
}

export function getEditSmsTemplateUrl(templateId: string) {
    const params = queryString.stringify({
        [QUERY_STRING_PROPS.EDIT_TEMPLATE_ID]: templateId,
    })

    return `${getTemplatesUrl()}?${params}`
}

function navigateWithDefaultToSubTotalUrl({
    bookingId,
    paymentType,
}: {
    bookingId: string
    paymentType?: PaymentType
}) {
    const baseUrl = getAdditionalBookingPaymentBaseUrl({ bookingId })
    let url = `${baseUrl}?${QUERY_STRING_PROPS.USE_SUBTOTAL}=true`

    if (paymentType) {
        url = `${url}&${QUERY_STRING_PROPS.PAYMENT_TYPE}=${paymentType}`
    }

    return navigate(url)
}

function getAdditionalBookingPaymentBaseUrl({
    bookingId,
}: {
    bookingId: string
}) {
    return `/booking/${bookingId}/additional-booking-payment`
}

export function navigateToOnboardings() {
    return navigate(ROUTES.ONBOARDINGS)
}

export function navigateToOnboardingForm(onboardingId: string) {
    return navigate(getOnboardingFormUrl(onboardingId))
}

export function navigateToEditSubscription(outfitterId: string) {
    return navigate(`${ROUTES.EDIT_SUBSCRIPTION}/${outfitterId}`)
}

export function navigateToEditCalendar(
    resourceType?: string | null,
    resourceId?: string | null
) {
    const url = getNavigateToEditCalendarUrl(resourceType, resourceId)
    return navigate(url)
}

export function navigateToEditCalendarForListing(listingId: string) {
    const url = getNavigateToEditCalendarUrlForListing(listingId)
    void navigate(url)
}

export function navigateToSmartSchedule({
    resourceType,
    resourceId,
    backRoute,
}: {
    resourceType?: string | null
    resourceId?: string | null
    backRoute?: string | null
}) {
    const url = getSmartScheduleUrl({ resourceType, resourceId, backRoute })

    return navigate(url)
}

export function navigateToOutfitterWithDelay(outfitterId: string) {
    const url = getOutfitterUrl(outfitterId)
    navigateWithDelay(url, 200)
}

export function navigateToBookingInviteForm({
    bookingInviteId,
    quoteRequestId,
    contactId,
    leadId,
}: {
    bookingInviteId?: string
    quoteRequestId?: string
    contactId?: string
    leadId?: string
} = {}) {
    const url = getBookingInviteFormUrl({
        bookingInviteId,
        quoteRequestId,
        contactId,
        leadId,
    })

    return navigate(url)
}

export function navigateToBookingInviteFormInNewTab({
    bookingInviteId,
    quoteRequestId,
    leadId,
    contactId,
}: {
    bookingInviteId?: string
    quoteRequestId?: string
    leadId?: string
    contactId?: string
}) {
    const url = getBookingInviteFormUrl({
        bookingInviteId,
        quoteRequestId,
        leadId,
        contactId,
    })

    navigateToExternalUrl({ url })
}

export function navigateToNewBookingFormInNewTab({
    leadId,
    source,
    contactId,
}: {
    leadId?: string
    source: string
    contactId?: string
}) {
    const url = getNewBookingUrl({ source, leadId, contactId })

    navigateToExternalUrl({ url })
}

export function navigateToManageListingPage(listingId: string) {
    const url = getManageListingUrl(listingId)

    return navigate(url)
}

export function navigateToManageListingsPage() {
    return navigate(ROUTES.MANAGE_LISTINGS)
}

export function navigateToBookingInviteDetails({
    bookingInviteId,
}: {
    bookingInviteId: string
}) {
    const url = getBookingInviteDetailsUrl({ bookingInviteId })

    return navigate(url)
}

function navigateWithDelay(url: string, delay = 200) {
    setTimeout(() => {
        void navigate(url)
    }, delay)
}

// URLS
export function getOutfitterUrl(
    outfitterId: string | null | undefined
): string {
    return `/outfitter/${outfitterId}`
}

export function getFeedbackUrl(): string {
    return ROUTES.FEEDBACK
}

export function getReviewGroupUrl(groupId: string): string {
    return `${ROUTES.FEEDBACK}/review-groups/${groupId}`
}

export function getGettingStartedUrlWithDomain() {
    return process.env.GATSBY_MB_ADMIN_URL
}

export function getWebUrl() {
    return process.env.GATSBY_MB_ROOT_URL!
}

function getMallardBayForGuidesUrl() {
    return "https://mallardbay.com/for-guides"
}

export function getWebsiteMallardBayForGuidesUrl() {
    return `${getMallardBayForGuidesUrl()}?utm_source=poweredby&utm_medium=website&utm_campaign=guide_acquisition`
}

export function getEmailMallardBayForGuidesUrl() {
    return `${getMallardBayForGuidesUrl()}?utm_source=poweredby&utm_medium=email&utm_campaign=guide_acquisition`
}

export function getRequestAQuoteEmbedContentUrl({
    outfitterId,
    noPadding = true,
}: {
    outfitterId: string
    noPadding?: boolean
}) {
    return `${getWebUrl()}/embed-content/request-a-quote?outfitter_id=${outfitterId}&no_padding=${noPadding}`
}

export function getWidgetEmbedContentUrl({
    outfitterId,
    listingId,
}: {
    outfitterId: string
    listingId?: string
}) {
    const params = new URLSearchParams()
    params.set(QUERY_STRING_PROPS.OUTFITTER_ID, outfitterId)

    if (listingId) params.set(QUERY_STRING_PROPS.LISTING_ID, listingId)

    return `${getWebUrl()}/embed-content/widget?${params.toString()}`
}

export function getFormEmbedContentUrl({
    formId,
    isESignatureRequired,
}: {
    formId: string
    isESignatureRequired?: boolean | null
}) {
    const path = isESignatureRequired
        ? "e-signature-form-submission"
        : "form-submission"

    // No styling is required for e-signature forms so web can set the styles itself
    const noStylingSuffix = isESignatureRequired
        ? "&no_styling=false"
        : "&no_styling=true"

    return `${getWebUrl()}/embed-content/${path}?form_id=${formId}${noStylingSuffix}`
}

export function getContactUrl(contactId: string) {
    return `/contacts/${contactId}`
}

export function getWebOutfitterUrl(outfitterSlug: string): string {
    return `${process.env.GATSBY_MB_ROOT_URL}/${outfitterSlug}`
}

export function getRequestSubscriptionChangesUrl() {
    return `${process.env.GATSBY_MB_ROOT_URL}/for-guides/subscription-requests`
}

export function getCalendarUrl() {
    return `/calendar`
}

export function navigateToCreateWidget(outfitterId: string) {
    return navigate(`/widget/new/${outfitterId}`)
}

function getWidgetUrl(widgetId: string) {
    return `/widget/${widgetId}`
}

export function navigateToEditWidget(widgetId: string) {
    return navigate(getWidgetUrl(widgetId))
}

export function navigateToWidgetInstructions(widgetId: string) {
    return navigate(`${getWidgetUrl(widgetId)}/instructions`)
}

export function getEditCalendarUrl() {
    return ROUTES.AVAILABILITY_CALENDAR
}

export function getBookingUrl(bookingId?: string, action?: string) {
    const baseUrl = `/booking/${bookingId}`
    if (action) {
        return `${baseUrl}?${QUERY_STRING_PROPS.ACTION}=${action}`
    }
    return baseUrl
}

export function navigateToVerifyMicrodeposit(
    bookingId: string,
    paymentId: string
) {
    const url = `${getWebUrl()}/booking/${bookingId}?verifyPaymentId=${paymentId}`
    return navigateToExternalUrl({ url })
}

export function getCalendarEventUrl({
    calendarEventId,
    backRoute,
}: {
    calendarEventId: string
    backRoute: string
}) {
    return `${ROUTES.CALENDAR_EVENT}/${calendarEventId}?${QUERY_STRING_PROPS.BACK_ROUTE}=${backRoute}`
}

export function getListingUrl({ listingId }: { listingId: string }) {
    return `${process.env.GATSBY_MB_ROOT_URL}/listing/${listingId}`
}

function getBookingInviteDetailsUrl({
    bookingInviteId,
}: {
    bookingInviteId: string
}) {
    return `${getSendQuoteRoute()}/${bookingInviteId}`
}

export function setResourceIdAndTypeUrlParams({
    resourceId,
    resourceType,
}: {
    resourceId: string
    resourceType: string
}) {
    return navigateToSearchParams({
        [QUERY_STRING_PROPS.RESOURCE_ID]: resourceId,
        [QUERY_STRING_PROPS.RESOURCE_TYPE]: resourceType,
    })
}

export function useGetResourceIdAndTypeFromUrl() {
    const resourceId =
        getUrlQueryParam(QUERY_STRING_PROPS.RESOURCE_ID) ?? undefined
    const resourceType = getUrlQueryParam(
        QUERY_STRING_PROPS.RESOURCE_TYPE
    )?.toUpperCase()

    return { resourceId, resourceType }
}

export function getQuoteRequestIdFromUrl() {
    return getUrlQueryParam(QUERY_STRING_PROPS.QUOTE_REQUEST_ID) ?? undefined
}

function getOnboardingFormUrl(onboardingId: string) {
    return `${ROUTES.ONBOARDINGS}/form/${onboardingId}`
}

function getBookingInviteFormUrl({
    bookingInviteId,
    quoteRequestId,
    leadId,
    contactId,
}: {
    bookingInviteId?: string
    quoteRequestId?: string
    leadId?: string
    contactId?: string
}) {
    const baseRoute = `${getSendQuoteRoute()}/form`

    const formUrl = bookingInviteId
        ? `${baseRoute}/${bookingInviteId}`
        : baseRoute

    const searchParams = getBookingInviteFormSearchParams({
        quoteRequestId,
        leadId,
        contactId,
    })

    return `${formUrl}${searchParams}`
}

function getBookingInviteFormSearchParams({
    quoteRequestId,
    leadId,
    contactId,
}: {
    quoteRequestId?: string
    leadId?: string
    contactId?: string
}) {
    const params = new URLSearchParams()

    if (quoteRequestId) {
        params.set(QUERY_STRING_PROPS.QUOTE_REQUEST_ID, quoteRequestId)
    }

    if (leadId) {
        params.set(QUERY_STRING_PROPS.LEAD_ID, leadId)
    }

    if (contactId) {
        params.set(QUERY_STRING_PROPS.CONTACT_ID, contactId)
    }

    const searchString = params.toString()
    return searchString ? `?${searchString}` : ""
}

function getSendQuoteRoute() {
    return "/send-quote"
}

function getNavigateToEditCalendarUrl(
    resourceType?: string | null,
    resourceId?: string | null
) {
    const baseUrl = "/calendar/edit"

    return getEditResourceUrl({ baseUrl, resourceType, resourceId })
}

function getSmartScheduleUrl({
    resourceType,
    resourceId,
    backRoute,
}: {
    resourceType?: string | null
    resourceId?: string | null
    backRoute?: string | null
}) {
    return getEditResourceUrl({
        baseUrl: ROUTES.SMART_SCHEDULE,
        resourceType,
        resourceId,
        backRoute,
    })
}

export function getEditResourceUrl({
    baseUrl,
    resourceType,
    resourceId,
    backRoute,
}: {
    baseUrl: string
    resourceType?: string | null
    resourceId?: string | null
    backRoute?: string | null
}) {
    const params = new URLSearchParams()
    // If there's a current month ts in the url, we'll pass it along so that
    // the calendar will open to the same month.
    const currentMonth = getUrlQueryParam(QUERY_STRING_PROPS.CURRENT_MONTH)

    if (resourceType) params.set(QUERY_STRING_PROPS.RESOURCE_TYPE, resourceType)
    if (resourceId) params.set(QUERY_STRING_PROPS.RESOURCE_ID, resourceId)
    if (currentMonth) {
        params.set(QUERY_STRING_PROPS.CURRENT_MONTH, currentMonth)
    }
    if (backRoute) {
        params.set(QUERY_STRING_PROPS.BACK_ROUTE, backRoute)
    }

    const paramsString = params.toString()

    return paramsString ? `${baseUrl}?${paramsString}` : baseUrl
}

function getNavigateToEditCalendarUrlForListing(listingId: string) {
    return getNavigateToEditCalendarUrl(ResourceType.Listing, listingId)
}

export function getAccountPageUrl() {
    return ROUTES.ACCOUNT_SETTINGS
}

export function getPaymentRequestUrl(paymentRequestId: string) {
    return `${process.env.GATSBY_MB_ROOT_URL}/payment-request/${paymentRequestId}`
}

export function getManageListingUrl(id: string) {
    return `/manage-listings/${id}`
}

export function getWebListingUrl(id: string) {
    const webHost = process.env.GATSBY_MB_ROOT_URL

    return `${webHost}/listing/${id}`
}

export function getFullRoute() {
    const defaultRoute = window.location.pathname
    const { href, host } = window.location
    if (!href || !host) {
        return defaultRoute
    }
    // full route will be pathname plus any query string or hash suffixes (i.e. /outfitters?search=name or /outfitter/{id}/#add-ons)
    const fullRoute = last(window.location.href.split(window.location.host))

    return fullRoute ?? defaultRoute
}

export function getHubspotCompanyLink(companyId: string) {
    return `${HUBSPOT_URL}/contacts/${process.env.GATSBY_HUBSPOT_ID}/record/0-2/${companyId}`
}

export function navigateToHome() {
    return navigate("/")
}

// Gatsby's navigate function performs some sort of prefetch which will expire
// one time use links (stripe account portals, etc). We should always use this
// helper for external links to avoid any issues like that.
export function navigateToExternalUrl({
    url,
    shouldUseReplace,
    shouldOpenInNewTab = true,
}: {
    url: string
    shouldUseReplace?: boolean
    shouldOpenInNewTab?: boolean
}) {
    if (shouldUseReplace) {
        window.location.replace(url)
        return
    }

    const target = shouldOpenInNewTab ? "_blank" : "_self"
    openInNewTab(url, target)
}

export function openInNewTab(url: string, target = "_blank") {
    window.open(url, target, "noopener,noreferrer")
}

export function navigateToSelfOnboard() {
    return navigate(ROUTES.SELF_ONBOARD)
}

export function getMailToUrl(email?: string | null) {
    if (!email) return ""
    return `mailto:${email}`
}

export function getTelUrl(phone: string) {
    return `tel:${phone}`
}

export function getKnowledgeLink() {
    return `${getWebUrl()}/pro/knowledge-base`
}

// Manage Listing Section Hashes
export const MANAGE_LISTING_HASHES = {
    CORE_LISTING_SETUP: "core-listing-setup",
    AVAILABILITY_PRICING: "availability-pricing",
    PAYMENTS: "payments",
    OPERATIONS_LOGISTICS: "operations-logistics",
    ADMIN_SETTINGS: "admin-settings",
} as const

// Manage Outfitter Section Hashes
export const MANAGE_OUTFITTER_HASHES = {
    ACCOUNT_BUSINESS_INFO: "account-business-info",
    MEDIA: "media",
    RESOURCES: "resources",
    BOOKING_GUEST_EXPERIENCE: "booking-guest-experience",
    PAYMENTS_FINANCIALS: "payments-financials",
    INTEGRATIONS_TOOLS: "integrations-tools",
    CUSTOM_EMAIL_DOMAIN: "custom-email-domain",
    ADMIN_SETTINGS: "admin-settings",
} as const

export const MANAGE_EVENT_ORGANIZER_HASHES = {
    PROFILE_AND_CONTACT_INFO: "profile-and-contact-info",
} as const

// Account Settings Section Hashes
export const ACCOUNT_SETTINGS_HASHES = {
    PROFILE: "profile",
    NOTIFICATION_PREFERENCES: "notification-preferences",
} as const

// eslint-disable-next-line max-lines-per-function -- this is a helper for the routes config
function getRoutes() {
    return {
        HOME: "/",
        HOME_NEW: "/home-new",
        ONBOARD: "/onboard",
        TEAM_MEMBER_ONBOARDING: "/team-member-onboarding",
        REPORTING_PAYOUTS: "/reporting/payouts/",
        REPORTING_PAYMENTS: "/reporting/payments/",
        REPORTING_UNPAID_BALANCES: "/reporting/unpaid-balances/",
        REPORTING_BUSINESS_PERFORMANCE: "/reporting/business-performance",
        REPORTING: "/reporting",
        QUOTES: "/send-quote",
        BOOKINGS: "/my-bookings",
        CALENDAR: "/calendar",
        MANAGE_LISTINGS: "/manage-listings",
        AVAILABILITY_CALENDAR: "/calendar/edit",
        DEFAULT_AVAILABILITY_RULES: "/calendar/default-availability-rules",
        NEW_DEFAULT_AVAILABILITY_RULE:
            "/calendar/default-availability-rules/new",
        ONBOARDINGS: "/onboardings",
        ONBOARDING_TASKS: "/onboarding-tasks",
        OUTFITTERS: "/outfitters",
        MY_OUTFITTER: "/my-outfitter",
        WEBSITE_BUILDER: "/web-builder",
        COUPONS: "/coupons",
        QUICK_PAY: "/quick-pay",
        AMENITIES: "/admin/assets/amenities",
        SPECIES: "/admin/assets/species",
        LICENSES: "/admin/assets/license-infos",
        BADGES: "/admin/assets/badges",
        ADMIN_LEADS: "/admin/admin-leads",
        ADMIN_BOOKING_INVITE_REQUESTS: "/admin/send-quote/requests",
        EDIT_SUBSCRIPTION: "/edit-subscription",
        SELF_ONBOARD: "/self-onboard/",
        STRIPE_ONBOARDING: "/stripe-onboarding",
        EVENT_ORGANIZER_SELF_ONBOARD: "/event-organizer-self-onboard",
        BILLING: "/billing",
        BILLING_PORTAL: "/billing-portal",
        ACCOUNT_PORTAL: "/account-portal",
        ACCOUNT_SETTINGS: "/account-settings",
        CONTACTS: "/contacts",
        NEW_CONTACT_LIST: "/contacts/lists/new",
        CONTACT_LIST: "/contacts/lists/:id",
        CONTACT_CUSTOM_PROPERTIES: "/contact-custom-properties",
        LEGACY_CLIENTS: "/clients",
        LEADS: "/leads",
        INTEGRATIONS: "/integrations",
        META: "/integrations/meta",
        SMS: "/integrations/sms",
        SMS_CONNECT: "/integrations/sms/registration",
        CUSTOM_EMAIL_DOMAIN: "/integrations/custom-email-domain",
        WEBSITE_WIDGET: "/integrations/website-widget",
        QUICK_BOOKS: "/integrations/quick-books",
        QUICK_BOOKS_ACCOUNT_DETAILS:
            "/integrations/quick-books/account-details",
        QUICK_BOOKS_GENERAL_SETTINGS:
            "/integrations/quick-books/general-settings",
        QUICK_BOOKS_CLASS_MAPPINGS: "/integrations/quick-books/class-mappings",
        PRODUCT_AND_SERVICE_MAPPINGS:
            "/integrations/quick-books/product-and-service-mappings",
        SMART_SCHEDULE: "/smart-schedule",
        CALENDAR_INTEGRATIONS: "/integrations/calendar",
        CALENDAR_IMPORT: "/integrations/calendar/import",
        CALENDAR_EXPORT: "/integrations/calendar/export",
        CALENDAR_EVENT: "/calendar/event",
        FORMS: "/forms",
        NEW_FORM: "/forms/new",
        EDIT_FORM: "/forms/edit",
        FEEDBACK: "/feedback",
        WEB_PERFORMANCE: "/web-performance",
        PAID_ADS_REPORTING: "/paid-ads-reporting",
        PAID_FACEBOOK_ADS: "/paid-facebook-ads",
        NEW_PAID_FACEBOOK_AD: "/paid-facebook-ads/new",
        TEMPLATES: "/templates",
        EDIT_TEMPLATE: "/edit-template",
        AUTOMATION_CENTER: "/automation-center",
        SOURCE_TRIPS: "/source-trips",
        AUCTIONS: "/auctions",
        APP_PROMPT_BLOCKING: "/app-prompt/blocking",
        CREATE_LISTING: "/create-listing",
    }
}

function normalizePathname(pathname: string): string {
    if (pathname.length > 1 && pathname.endsWith("/")) {
        return pathname.slice(0, -1)
    }
    return pathname
}

/* eslint-enable max-lines */
